// Video Modal Component
// Modal for creating and editing tutorial videos
// Supports both create and edit modes with tag management

import React, { useState, useEffect, useCallback } from 'react';
import { RevoltModal } from '../../../../components/RevoltModal';
import { RevoltInput } from '../../../../components/RevoltInput';
import { RevoltTextarea } from '../../../../components/RevoltTextarea';
import { RevoltButton } from '../../../../components/RevoltButton';
import DropdownSelect from '../../../../components/DropdownSelect';
import { extractVideoId, getEmbedUrl } from '../../utils';
import './VideoModal.css';
import { logger } from '../../../../core';

export function VideoModal({
  isOpen,
  onClose,
  mode = 'create', // 'create' or 'edit'
  initialData = {},
  defaultCategoryId = '', // Default category for create mode
  onSubmit,
  onViewInLearnPage
}) {
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [supportedVideoFormat, setSupportedVideoFormat] = useState('');
  const [videoDisplayFormat, setVideoDisplayFormat] = useState('2x1'); // 1x1 or 2x1
  const [duration, setDuration] = useState('');
  const [date, setDate] = useState('');
  const [referencedVideos, setReferencedVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Referenced videos search state
  const [videoSearchQuery, setVideoSearchQuery] = useState('');
  const [videoSearchResults, setVideoSearchResults] = useState([]);
  const [selectedReferenceType, setSelectedReferenceType] = useState('related');
  const [isSearchingVideos, setIsSearchingVideos] = useState(false);

  // Category options for dropdown
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Error handling
  const [errorMessage, setErrorMessage] = useState('');

  // Video preview loading state to prevent CSP violations
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  // Load categories from API
  const loadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetch('/api/learn/categories');
      if (response.ok) {
        const data = await response.json();
        setCategoryOptions(data.categories.map(cat => ({
          value: cat.id,
          label: cat.name
        })));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Load categories when modal opens
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  // Search for videos to reference
  const searchVideos = useCallback(async (query) => {
    if (!query.trim()) {
      setVideoSearchResults([]);
      return;
    }

    setIsSearchingVideos(true);
    try {
      const response = await fetch(`/api/learn/videos?search=${encodeURIComponent(query.trim())}&limit=5`);
      if (response.ok) {
        const data = await response.json();
        // Filter out the current video if we're editing
        const filteredResults = data.videos.filter(video =>
          mode !== 'edit' || video.id !== initialData?.id
        );
        setVideoSearchResults(filteredResults);
      } else {
        setVideoSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching videos:', error);
      setVideoSearchResults([]);
    } finally {
      setIsSearchingVideos(false);
    }
  }, [mode, initialData?.id]);

  // Debounced video search
  useEffect(() => {
    if (!videoSearchQuery.trim()) {
      setVideoSearchResults([]);
      return;
    }

    const debounceTimer = setTimeout(() => {
      searchVideos(videoSearchQuery);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [videoSearchQuery, searchVideos]);

  // Reset form when modal opens/closes or mode/data changes
  useEffect(() => {
    if (isOpen) {
      // Delay video preview loading to prevent CSP violations
      setShowVideoPreview(false);

      if (mode === 'edit' && initialData) {
        logger.log({
          tags: 'learn|video-modal|edit',
          color1: 'cyan'
        }, 'VideoModal edit mode - initialData.videoDisplayFormat:', initialData.videoDisplayFormat);
        setName(initialData.name || '');
        setDescription(initialData.description || '');
        setShortDescription(initialData.shortDescription || '');
        setCategoryId(initialData.categoryId || '');
        setVideoUrl(initialData.videoUrl || '');
        setSupportedVideoFormat(initialData.supportedVideoFormat || '');
        setVideoDisplayFormat(initialData.videoDisplayFormat || '1x1');
        setDuration(initialData.duration || '');
        setDate(initialData.date || '');
        setReferencedVideos([...(initialData.referencedVideos || [])]);
        setTags([...(initialData.tags || [])]);
      } else {
        // Create mode - reset to defaults
        setName('');
        setDescription('');
        setShortDescription('');
        setCategoryId(defaultCategoryId);
        setVideoUrl('');
        setSupportedVideoFormat('');
        setVideoDisplayFormat('2x1');
        setDuration('');
        setDate('');
        setReferencedVideos([]);
        setTags([]);
      }
      setNewTag('');
      setIsSubmitting(false);

      // Delay showing video preview to prevent CSP violations
      const timer = setTimeout(() => {
        setShowVideoPreview(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // Modal is closing, hide preview immediately
      setShowVideoPreview(false);
    }
  }, [isOpen, mode, initialData, defaultCategoryId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear any previous errors
    setErrorMessage('');

    // Validation
    if (!name.trim()) {
      setErrorMessage('Video name is required');
      return;
    }

    if (!categoryId || categoryId === '') {
      setErrorMessage('Please select a category');
      return;
    }

    // Basic URL validation for video
    if (videoUrl.trim() && !extractVideoId(videoUrl.trim())) {
      setErrorMessage('Please enter a valid YouTube or Vimeo URL');
      return;
    }

    setIsSubmitting(true);

    try {
      const videoData = {
        name: name.trim(),
        description: description.trim(),
        shortDescription: shortDescription.trim(),
        categoryId: categoryId,
        videoUrl: videoUrl.trim(),
        supportedVideoFormat: supportedVideoFormat || null,
        videoDisplayFormat: videoDisplayFormat,
        duration: duration.trim(),
        date: date,
        referencedVideos: [...referencedVideos],
        tags: [...tags],
        ...(mode === 'edit' && initialData?.id && { id: initialData.id })
      };

      logger.log({
        tags: 'learn|video-modal|submit',
        color1: 'cyan'
      }, 'VideoModal submit - videoDisplayFormat:', videoDisplayFormat);
      logger.log({
        tags: 'learn|video-modal|submit',
        color1: 'cyan'
      }, 'VideoModal submit - full videoData:', videoData);

      // Call the onSubmit callback with the data
      if (onSubmit) {
        await onSubmit(videoData);
      }

      // Success - close modal
      onClose();
    } catch (error) {
      console.error('Error saving video:', error);
      setErrorMessage(error.message || 'Failed to save video. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = () => {
    const tagValue = newTag.trim().toLowerCase();
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Add a video reference
  const addVideoReference = (video) => {
    if (!video || !selectedReferenceType) return;

    // Check if this reference already exists
    const existingRef = referencedVideos.find(ref =>
      ref.videoId === video.id && ref.type === selectedReferenceType
    );

    if (existingRef) {
      // Already exists, don't add duplicate
      return;
    }

    const newReference = {
      videoId: video.id,
      type: selectedReferenceType
    };

    setReferencedVideos([...referencedVideos, newReference]);
    setVideoSearchQuery('');
    setVideoSearchResults([]);
  };

  const modalTitle = mode === 'create' ? 'Create Tutorial Video' : 'Edit Tutorial Video';
  const submitButtonText = mode === 'create' ? 'Create Video' : 'Save Changes';

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      containerClassName="video-modal-container modal-wide"
    >
      <div className="modal-scrollable-content"    >
      {errorMessage && (
        <div className="modal-error-message">
          <i className="fa-solid fa-exclamation-triangle"></i>
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="video-modal-form">
        <div className="video-modal-columns">
          {/* Left Column - Basic Info */}
          <div className="video-modal-left-column">
            {/* Name Field */}
            <div className="form-group">
              <label htmlFor="video-name" className="form-label">
                Name <span className="required">*</span>
              </label>
              <RevoltInput
                id="video-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter video name..."
                required
                maxLength={100}
              />
            </div>

            {/* Category Field */}
            <div className="form-group">
              <label className="form-label">
                Category <span className="required">*</span>
              </label>
              <DropdownSelect
                value={categoryId}
                onValueChange={setCategoryId}
                options={categoryOptions}
                placeholder={categoriesLoading ? "Loading categories..." : "Select a category..."}
                disabled={categoriesLoading}
                required
              />
              <div className="form-help">
                Choose the category this video belongs to.
              </div>
            </div>

            {/* Short Description Field */}
            <div className="form-group">
              <label htmlFor="video-short-description" className="form-label">
                Short Description <span className="required">*</span>
              </label>
              <RevoltTextarea
                id="video-short-description"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Enter a brief description for video cards..."
                rows={3}
                required
              />
              <div className="form-help">
                This short description appears on video cards. Keep it concise and engaging.
              </div>
            </div>

            {/* Description Field */}
            <div className="form-group">
              <label htmlFor="video-description" className="form-label">
                Full Description
              </label>
              <RevoltTextarea
                id="video-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter the complete video description..."
                rows={6}
              />
              <div className="form-help">
                The full description appears when viewing the complete video details.
              </div>
            </div>

          </div>

          {/* Middle Column - Video Settings */}
          <div className="video-modal-middle-column">
            {/* Tags Section */}
            <div className="form-group">
              <label className="form-label">Tags</label>

              {/* Current Tags */}
              {tags.length > 0 && (
                <div className="tags-list">
                  {tags.map((tag, index) => (
                    <span key={index} className="tag-item">
                      {tag}
                      <button
                        type="button"
                        className="tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                        aria-label={`Remove ${tag} tag`}
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add New Tag */}
              <div className="add-tag-group">
                <RevoltInput
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value.replace(/\s+/g, '-'))}
                  onKeyPress={handleTagKeyPress}
                  placeholder="Add a tag..."
                  maxLength={30}
                />
                <RevoltButton
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={handleAddTag}
                  disabled={!newTag.trim() || tags.includes(newTag.trim().toLowerCase())}
                >
                  <i className="fa-solid fa-plus"></i>
                  Add
                </RevoltButton>
              </div>

              <div className="form-help">
                Tags help categorize videos. Use lowercase, separate words with dashes (e.g., "getting-started").
              </div>
            </div>

            {/* Video Format Fields */}
            <div className="form-row">
              {/* Supported Video Format */}
              <div className="form-group">
                <label className="form-label">
                  Video Platform
                </label>
                <DropdownSelect
                  value={supportedVideoFormat}
                  onValueChange={setSupportedVideoFormat}
                  options={[
                    { value: 'youtube', label: 'YouTube' },
                    { value: 'vimeo', label: 'Vimeo' },
                    { value: 'mp4', label: 'MP4 File' }
                  ]}
                  placeholder="Select video platform..."
                />
                <div className="form-help">
                  Choose the platform where this video is hosted.
                </div>
              </div>

              {/* Display Format */}
              <div className="form-group">
                <label className="form-label">
                  Display Format
                </label>
                <DropdownSelect
                  value={videoDisplayFormat}
                  onValueChange={setVideoDisplayFormat}
                  options={[
                    { value: '1x1', label: '1x1 (Square)' },
                    { value: '2x1', label: '2x1 (Wide)' }
                  ]}
                  placeholder="Select display format..."
                />
                <div className="form-help">
                  Choose how the video should be displayed in the grid.
                </div>
              </div>

              {/* Duration Field */}
              <div className="form-group">
                <label htmlFor="video-duration" className="form-label">
                  Duration
                </label>
                <RevoltInput
                  id="video-duration"
                  type="text"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g., 5:23, 1:15:30"
                />
                <div className="form-help">
                  Video duration in MM:SS or HH:MM:SS format.
                </div>
              </div>

              {/* Date Field */}
              <div className="form-group">
                <label htmlFor="video-date" className="form-label">
                  Date
                </label>
                <RevoltInput
                  id="video-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
                <div className="form-help">
                  Video publish date (auto-populated with today for new videos).
                </div>
              </div>
            </div>

            {/* Video URL Field */}
            <div className="form-group">
              <label htmlFor="video-url" className="form-label">
                Video URL
              </label>
              <RevoltInput
                id="video-url"
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
              />
              <div className="form-help">
                Supported platforms: YouTube, Vimeo. The video will be embedded in the player.
              </div>
            </div>
          </div>

          {/* Right Column - Preview & References */}
          <div className="video-modal-right-column">
            {/* Video Preview */}
            <div className="form-group">
              <label className="form-label">
                {videoUrl ? 'Video Preview' : 'Video Preview (Enter URL above)'}
              </label>
              <div className="video-preview-container">
                {videoUrl ? (() => {
                  const videoInfo = extractVideoId(videoUrl);
                  const embedUrl = getEmbedUrl(videoInfo);


                  if (embedUrl && showVideoPreview) {
                    return (
                      <div className="video-preview-wrapper">
                        <iframe
                          src={embedUrl}
                          title="Video Preview"
                          className="video-preview-iframe"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    );
                  } else if (embedUrl && !showVideoPreview) {
                    return (
                      <div className="video-preview-placeholder">
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        <span>Loading video preview...</span>
                      </div>
                    );
                  } else {
                    return (
                      <div className="video-preview-error">
                        <i className="fa-solid fa-exclamation-triangle"></i>
                        <span>Unable to preview video. Please check the URL format.</span>
                      </div>
                    );
                  }
                })() : (
                  <div className="video-preview-placeholder">
                    <i className="fa-solid fa-video"></i>
                    <span>Enter a video URL above to see the preview</span>
                  </div>
                )}
              </div>
            </div>

            {/* Referenced Videos Section */}
            <div className="form-group">
              <label className="form-label">Referenced Videos</label>

              {/* Add New Reference */}
              <div className="add-reference-section">
                <div className="reference-type-selector">
                  <DropdownSelect
                    value={selectedReferenceType}
                    onValueChange={setSelectedReferenceType}
                    options={[
                      { value: 'related', label: 'Related' },
                      { value: 'next', label: 'Next in Series' },
                      { value: 'previous', label: 'Previous in Series' }
                    ]}
                    placeholder="Select type..."
                  />
                </div>
                <div className="video-search-input">
                  <RevoltInput
                    type="text"
                    value={videoSearchQuery}
                    onChange={(e) => setVideoSearchQuery(e.target.value)}
                    placeholder="Search for videos to reference..."
                  />
                </div>
              </div>

              {/* Video Search Results */}
              {videoSearchQuery && (
                <div className="video-search-results">
                  {isSearchingVideos ? (
                    <div className="search-loading">
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      <span>Searching...</span>
                    </div>
                  ) : videoSearchResults.length > 0 ? (
                    <div className="search-results-list">
                      {videoSearchResults.map((video) => (
                        <button
                          key={video.id}
                          type="button"
                          className="search-result-item"
                          onClick={() => addVideoReference(video)}
                        >
                          <div className="search-result-content">
                            <span className="search-result-title">{video.name}</span>
                            <span className="search-result-category">{video.categoryId}</span>
                          </div>
                          <i className="fa-solid fa-plus"></i>
                        </button>
                      ))}
                    </div>
                  ) : videoSearchQuery.length > 2 ? (
                    <div className="search-no-results">
                      <span>No videos found matching "{videoSearchQuery}"</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Current Referenced Videos */}
              {referencedVideos.length > 0 && (
                <div className="referenced-videos-list">
                  {referencedVideos.map((ref, index) => (
                    <div key={index} className="referenced-video-item">
                      <span className="referenced-video-type">{ref.type}</span>
                      <span className="referenced-video-id">{ref.videoId}</span>
                      <button
                        type="button"
                        className="referenced-video-remove"
                        onClick={() => {
                          setReferencedVideos(referencedVideos.filter((_, i) => i !== index));
                        }}
                        aria-label={`Remove ${ref.type} video reference`}
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-help">
                Link this video to other videos (related content, next/previous in series).
              </div>
            </div>
          </div>
        </div>
        </form>
      </div>

      <div className="video-modal-footer">
        {/* Modal Actions */}
        <div className="modal-actions">
          <RevoltButton
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>

          {/* View in Learn Page button - only in edit mode */}
          {mode === 'edit' && onViewInLearnPage && (
            <RevoltButton
              type="button"
              variant="secondary"
              onClick={() => onViewInLearnPage(initialData)}
              disabled={isSubmitting}
            >
              <i className="fa-solid fa-external-link"></i>
              View in Learn Page
            </RevoltButton>
          )}
          <RevoltButton
            variant="primary"
            disabled={!name.trim() || !categoryId || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                {mode === 'create' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              submitButtonText
            )}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}